import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  ActivityIndicator,
  ScrollView,
  StyleSheet,
  TouchableOpacity,
  View,
} from 'react-native';

import { ThemedText } from '../ThemedText';
import { useScale } from '@/hooks/useScale';

const SAMPLE_VIDEO_URL =
  'https://commondatastorage.googleapis.com/gtv-videos-bucket/sample/BigBuckBunny.mp4';

const CACHE_NAME = 'video-cache';

type CacheMode = 'cache' | 'stream';

type DownloadState =
  | { step: 'idle' }
  | { step: 'downloading'; progress: number }
  | { step: 'downloaded' }
  | { step: 'streaming' }
  | { step: 'error'; message: string };

interface FetchLogEntry {
  source: 'cache' | 'network';
  range: string | null;
  timestamp: number;
}

const MAX_LOG_ENTRIES = 20;

const DownloadVideoDemo: React.FC = () => {
  const styles = useStyles();
  const videoRef = useRef<HTMLVideoElement>(null);

  const [state, setState] = useState<DownloadState>({ step: 'idle' });
  const [isPlaying, setIsPlaying] = useState(false);
  const [fetchLog, setFetchLog] = useState<FetchLogEntry[]>([]);
  const [cacheMode, setCacheMode] = useState<CacheMode>('cache');

  // Check if video is already cached on mount
  useEffect(() => {
    if (cacheMode === 'stream') {
      setState({ step: 'streaming' });
      return;
    }
    if (typeof caches === 'undefined') return;
    caches
      .open(CACHE_NAME)
      .then((cache) => cache.match(SAMPLE_VIDEO_URL))
      .then((match) => {
        if (match) {
          setState({ step: 'downloaded' });
        }
      })
      .catch(() => {});
  }, []);

  // Listen for SW postMessage with fetch source info
  useEffect(() => {
    const sw = navigator.serviceWorker;
    if (!sw) return;

    const handler = (event: MessageEvent) => {
      const data = event.data;
      if (data?.type !== 'FETCH_SOURCE') return;
      if (data.url !== SAMPLE_VIDEO_URL) return;

      setFetchLog((prev) => {
        const next = [
          ...prev,
          {
            source: data.source as 'cache' | 'network',
            range: data.range ?? null,
            timestamp: Date.now(),
          },
        ];
        // Keep only the last N entries
        return next.slice(-MAX_LOG_ENTRIES);
      });
    };

    sw.addEventListener('message', handler);
    return () => sw.removeEventListener('message', handler);
  }, []);

  const handleDownload = useCallback(async () => {
    setState({ step: 'downloading', progress: 0 });

    try {
      const response = await fetch(SAMPLE_VIDEO_URL);
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`);
      }

      const clone = response.clone();

      // Browser streams the clone to cache storage (disk) — not held in JS memory
      const cache = await caches.open(CACHE_NAME);
      const cachePromise = cache.put(SAMPLE_VIDEO_URL, clone);

      // Read the original stream only for progress tracking — chunks are discarded
      const contentLength = Number(response.headers.get('Content-Length')) || 0;
      const reader = response.body?.getReader();
      if (!reader) {
        throw new Error('ReadableStream not supported');
      }

      let receivedLength = 0;

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        receivedLength += value.length;
        if (contentLength > 0) {
          setState({
            step: 'downloading',
            progress: Math.round((receivedLength / contentLength) * 100),
          });
        }
      }

      await cachePromise;

      setState({ step: 'downloaded' });
    } catch (err) {
      setState({
        step: 'error',
        message: err instanceof Error ? err.message : 'Download failed',
      });
    }
  }, []);

  const handleDelete = useCallback(async () => {
    try {
      const cache = await caches.open(CACHE_NAME);
      await cache.delete(SAMPLE_VIDEO_URL);
      if (videoRef.current) {
        videoRef.current.pause();
        videoRef.current.removeAttribute('src');
        videoRef.current.load();
      }
      setIsPlaying(false);
      setState({ step: 'idle' });
    } catch (err) {
      console.error('[DownloadVideoDemo] Failed to delete:', err);
    }
  }, []);

  const toggleCacheMode = useCallback(async () => {
    // Stop playback and detach video
    if (videoRef.current) {
      videoRef.current.pause();
      videoRef.current.removeAttribute('src');
      videoRef.current.load();
    }
    setIsPlaying(false);
    setFetchLog([]);

    if (cacheMode === 'cache') {
      // Switching to stream mode — delete cached video if present
      try {
        const cache = await caches.open(CACHE_NAME);
        await cache.delete(SAMPLE_VIDEO_URL);
      } catch {}
      setCacheMode('stream');
      setState({ step: 'streaming' });
    } else {
      // Switching to cache mode — check if already cached
      setCacheMode('cache');
      try {
        const cache = await caches.open(CACHE_NAME);
        const match = await cache.match(SAMPLE_VIDEO_URL);
        setState(match ? { step: 'downloaded' } : { step: 'idle' });
      } catch {
        setState({ step: 'idle' });
      }
    }
  }, [cacheMode]);

  const togglePlayback = useCallback(() => {
    const video = videoRef.current;
    if (!video) return;

    if (video.paused) {
      video.play();
    } else {
      video.pause();
    }
  }, []);

  // Sync isPlaying state with video element events
  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;

    const onPlay = () => setIsPlaying(true);
    const onPause = () => setIsPlaying(false);

    video.addEventListener('play', onPlay);
    video.addEventListener('pause', onPause);
    return () => {
      video.removeEventListener('play', onPlay);
      video.removeEventListener('pause', onPause);
    };
  }, [state.step]);

  // Set video src when downloaded — the service worker will intercept and serve from cache
  useEffect(() => {
    if (state.step === 'downloaded' && videoRef.current) {
      videoRef.current.src = SAMPLE_VIDEO_URL;
    }
  }, [state.step]);

  // Set video src for streaming mode
  useEffect(() => {
    if (state.step === 'streaming' && videoRef.current) {
      videoRef.current.src = SAMPLE_VIDEO_URL;
    }
  }, [state.step]);

  const cacheHits = fetchLog.filter((e) => e.source === 'cache').length;
  const networkFetches = fetchLog.filter((e) => e.source === 'network').length;

  return (
    <View style={styles.container}>
      <ThemedText type="subtitle">
        Download Video Demo - Web (Cache API + Service Worker)
      </ThemedText>

      <View style={styles.row}>
        <TouchableOpacity
          style={[
            styles.toggleButton,
            cacheMode === 'cache'
              ? styles.toggleButtonActive
              : styles.toggleButtonInactive,
          ]}
          onPress={toggleCacheMode}
        >
          <ThemedText style={styles.toggleButtonText}>
            {cacheMode === 'cache' ? 'Cache Mode ON' : 'Cache Mode OFF'}
          </ThemedText>
        </TouchableOpacity>
        <ThemedText style={styles.mutedText}>
          {cacheMode === 'cache'
            ? 'Download first, then serve from SW cache'
            : 'Stream directly from network'}
        </ThemedText>
      </View>

      {state.step === 'idle' && cacheMode === 'cache' && (
        <>
          <ThemedText style={styles.mutedText}>
            Downloads a sample video to the browser Cache API, then plays it via
            service worker interception with range request support.
          </ThemedText>
          <TouchableOpacity style={styles.button} onPress={handleDownload}>
            <ThemedText>Download Video</ThemedText>
          </TouchableOpacity>
        </>
      )}

      {state.step === 'downloading' && (
        <View style={styles.downloadingRow}>
          <ActivityIndicator size="small" color="#687dcf" />
          <ThemedText style={styles.mutedText}>
            Downloading… {state.progress}%
          </ThemedText>
        </View>
      )}

      {state.step === 'downloaded' && (
        <>
          <ThemedText style={styles.mutedText}>
            Video cached in browser. Served by service worker.
          </ThemedText>
          <View style={styles.videoContainer}>
            <video
              ref={videoRef}
              style={{ width: '100%', maxHeight: 400, backgroundColor: '#000' }}
              controls
              loop
            />
          </View>
          <View style={styles.row}>
            <TouchableOpacity style={styles.button} onPress={togglePlayback}>
              <ThemedText>{isPlaying ? 'Pause' : 'Play'}</ThemedText>
            </TouchableOpacity>
            <TouchableOpacity
              style={styles.deleteButton}
              onPress={handleDelete}
            >
              <ThemedText>Delete File</ThemedText>
            </TouchableOpacity>
          </View>
        </>
      )}

      {state.step === 'streaming' && (
        <>
          <ThemedText style={styles.mutedText}>
            Streaming directly from network (download-to-cache disabled).
          </ThemedText>
          <View style={styles.videoContainer}>
            <video
              ref={videoRef}
              style={{ width: '100%', maxHeight: 400, backgroundColor: '#000' }}
              controls
              loop
            />
          </View>
          <View style={styles.row}>
            <TouchableOpacity style={styles.button} onPress={togglePlayback}>
              <ThemedText>{isPlaying ? 'Pause' : 'Play'}</ThemedText>
            </TouchableOpacity>
          </View>
        </>
      )}

      {state.step === 'error' && (
        <>
          <ThemedText style={styles.errorText}>{state.message}</ThemedText>
          <TouchableOpacity style={styles.button} onPress={handleDownload}>
            <ThemedText>Retry</ThemedText>
          </TouchableOpacity>
        </>
      )}

      {/* Request Log Panel */}
      {(state.step === 'downloaded' || state.step === 'streaming') && (
        <View style={styles.logPanel}>
          <View style={styles.logHeader}>
            <ThemedText type="defaultSemiBold">Request Log</ThemedText>
            <TouchableOpacity
              style={styles.clearButton}
              onPress={() => setFetchLog([])}
            >
              <ThemedText style={styles.clearButtonText}>Clear Log</ThemedText>
            </TouchableOpacity>
          </View>

          <View style={styles.statsRow}>
            <ThemedText style={styles.mutedText}>
              Total: {fetchLog.length}
            </ThemedText>
            <ThemedText style={styles.cacheText}>Cache: {cacheHits}</ThemedText>
            <ThemedText style={styles.networkText}>
              Network: {networkFetches}
            </ThemedText>
          </View>

          <ScrollView style={styles.logList}>
            {fetchLog.length === 0 && (
              <ThemedText style={styles.emptyLogText}>
                No requests yet. Play the video to see fetch activity.
              </ThemedText>
            )}
            {[...fetchLog].reverse().map((entry, i) => (
              <View key={fetchLog.length - 1 - i} style={styles.logEntry}>
                <View
                  style={[
                    styles.sourceBadge,
                    entry.source === 'cache'
                      ? styles.cacheBadge
                      : styles.networkBadge,
                  ]}
                >
                  <ThemedText style={styles.badgeText}>
                    {entry.source.toUpperCase()}
                  </ThemedText>
                </View>
                <ThemedText style={styles.logEntryText}>
                  {entry.range ?? 'full request'}
                </ThemedText>
                <ThemedText style={styles.logTimestamp}>
                  {new Date(entry.timestamp).toLocaleTimeString()}
                </ThemedText>
              </View>
            ))}
          </ScrollView>
        </View>
      )}
    </View>
  );
};

function useStyles() {
  const scale = useScale();

  return StyleSheet.create({
    container: {
      gap: 8 * scale,
    },
    mutedText: {
      opacity: 0.8,
    },
    row: {
      flexDirection: 'row',
      gap: 12 * scale,
      alignItems: 'center',
    },
    button: {
      paddingVertical: 10 * scale,
      paddingHorizontal: 14 * scale,
      borderRadius: 8 * scale,
      borderWidth: 1,
      borderColor: '#808080',
      alignSelf: 'flex-start',
      backgroundColor: '#687dcf',
    },
    deleteButton: {
      paddingVertical: 10 * scale,
      paddingHorizontal: 14 * scale,
      borderRadius: 8 * scale,
      borderWidth: 1,
      borderColor: '#808080',
      alignSelf: 'flex-start',
      backgroundColor: '#c0392b',
    },
    toggleButton: {
      paddingVertical: 8 * scale,
      paddingHorizontal: 12 * scale,
      borderRadius: 8 * scale,
      borderWidth: 1,
    },
    toggleButtonActive: {
      backgroundColor: 'rgba(46, 204, 113, 0.25)',
      borderColor: '#2ecc71',
    },
    toggleButtonInactive: {
      backgroundColor: 'rgba(243, 156, 18, 0.25)',
      borderColor: '#f39c12',
    },
    toggleButtonText: {
      fontSize: 13 * scale,
      fontWeight: '600',
    },
    downloadingRow: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: 8 * scale,
    },
    videoContainer: {
      width: '100%',
    },
    errorText: {
      color: '#ff6b6b',
    },
    // Request Log styles
    logPanel: {
      marginTop: 12 * scale,
      borderWidth: 1,
      borderColor: '#444',
      borderRadius: 8 * scale,
      padding: 12 * scale,
      gap: 8 * scale,
    },
    logHeader: {
      flexDirection: 'row',
      justifyContent: 'space-between',
      alignItems: 'center',
    },
    clearButton: {
      paddingVertical: 4 * scale,
      paddingHorizontal: 10 * scale,
      borderRadius: 6 * scale,
      borderWidth: 1,
      borderColor: '#666',
    },
    clearButtonText: {
      fontSize: 12 * scale,
      opacity: 0.8,
    },
    statsRow: {
      flexDirection: 'row',
      gap: 16 * scale,
    },
    cacheText: {
      color: '#2ecc71',
    },
    networkText: {
      color: '#f39c12',
    },
    logList: {
      maxHeight: 240 * scale,
    },
    logEntry: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: 8 * scale,
      paddingVertical: 3 * scale,
      borderBottomWidth: StyleSheet.hairlineWidth,
      borderBottomColor: '#333',
    },
    sourceBadge: {
      paddingVertical: 2 * scale,
      paddingHorizontal: 6 * scale,
      borderRadius: 4 * scale,
      minWidth: 70 * scale,
      alignItems: 'center',
    },
    cacheBadge: {
      backgroundColor: 'rgba(46, 204, 113, 0.2)',
    },
    networkBadge: {
      backgroundColor: 'rgba(243, 156, 18, 0.2)',
    },
    badgeText: {
      fontSize: 11 * scale,
      fontWeight: '700',
    },
    logEntryText: {
      flex: 1,
      fontSize: 12 * scale,
      opacity: 0.8,
    },
    logTimestamp: {
      fontSize: 11 * scale,
      opacity: 0.5,
    },
    emptyLogText: {
      opacity: 0.5,
      fontStyle: 'italic',
      paddingVertical: 8 * scale,
    },
  });
}

export default DownloadVideoDemo;
